import { Check, Loader2 } from 'lucide-react';
import { WOT_PHASE_STEPS, type WotPhase } from '@/hooks/useWotScore';
import { cn } from '@/lib/utils';

/** Live checklist shown while the trust report is being assembled. */
export function WotProgress({ phase }: { phase: WotPhase }) {
  const currentIndex = WOT_PHASE_STEPS.findIndex((s) => s.key === phase);
  const progress = Math.max(
    8,
    Math.round(((currentIndex + 1) / WOT_PHASE_STEPS.length) * 100),
  );

  return (
    <div className="mx-auto w-full max-w-md rounded-xl border border-primary/30 bg-card/80 backdrop-blur-sm p-6">
      <div className="mb-5 h-1.5 w-full overflow-hidden rounded-full bg-primary/15">
        <div
          className="h-full rounded-full bg-gradient-to-r from-primary to-glow transition-all duration-700 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
      <ul className="space-y-3.5">
        {WOT_PHASE_STEPS.map((step, i) => {
          const done = i < currentIndex;
          const active = i === currentIndex;
          return (
            <li key={step.key} className="flex items-center gap-3 text-sm">
              <span
                className={cn(
                  'flex size-6 shrink-0 items-center justify-center rounded-full border transition-colors',
                  done && 'border-primary bg-primary text-white',
                  active && 'border-glow text-glow',
                  !done && !active && 'border-primary/25 text-muted-foreground/50',
                )}
              >
                {done ? (
                  <Check className="size-3.5" />
                ) : active ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <span className="size-1.5 rounded-full bg-current" />
                )}
              </span>
              <span
                className={cn(
                  done && 'text-muted-foreground line-through decoration-primary/40',
                  active && 'text-foreground font-medium',
                  !done && !active && 'text-muted-foreground/60',
                )}
              >
                {step.label}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
